import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import Seo from "../components/Seo";
import { PageHero } from "../components/Section";
import { useApp } from "../context/AppContext";
import { useReveal } from "../hooks/useReveal";
import { blogData, getBlogFaq } from "../data/blogData";
import { getBlogPost } from "../services/api";
import { Calendar, User, ArrowLeft, HelpCircle } from "lucide-react";

const BlogDetail = () => {
  const { slug } = useParams();
  const { lang, t } = useApp();
  const [post, setPost] = useState(() => blogData.find((p) => p.slug === slug) || null);
  useReveal([post, lang]);

  useEffect(() => {
    let active = true;
    getBlogPost(slug)
      .then((d) => { if (active && d) setPost(d); })
      .catch(() => { if (active && !post) setPost(blogData.find((p) => p.slug === slug) || blogData[0]); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!post) return <div className="pt-40 text-center text-ink/50">…</div>;

  const faq = getBlogFaq(post, lang);
  const bodyHtml = post.body ? (post.body[lang] || post.body.en) : "";
  const fmt = (d) => new Date(d).toLocaleDateString(lang === "de" ? "de-DE" : "en-US", { year: "numeric", month: "long", day: "numeric" });

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "BlogPosting", headline: post.title[lang] || post.title.en, image: post.img, datePublished: post.date, author: { "@type": "Organization", name: post.author || "My Own Cuvée" }, publisher: { "@type": "Organization", name: "My Own Cuvée" }, description: post.excerpt[lang] || post.excerpt.en },
      { "@type": "FAQPage", mainEntity: faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
    ],
  };

  return (
    <div className="pt-20 bg-cream min-h-screen">
      <Seo title={post.title[lang] || post.title.en} description={post.excerpt[lang] || post.excerpt.en} image={post.img} path={`/blog/${post.slug}`} type="article" lang={lang} jsonLd={jsonLd} />
      <PageHero title={post.title[lang] || post.title.en} subtitle={fmt(post.date)} image={post.img} />
      <article className="max-w-3xl mx-auto px-6 py-14">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-wine hover:text-wine-dark text-sm font-medium mb-6"><ArrowLeft className="w-4 h-4" /> Blog</Link>
        <div className="flex items-center gap-5 text-sm text-ink/50 mb-8">
          <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {fmt(post.date)}</span>
          <span className="flex items-center gap-1.5"><User className="w-4 h-4" /> {post.author || "My Own Cuvée"}</span>
        </div>
        {bodyHtml ? (
          <div className="blog-content reveal text-ink/80 text-lg leading-relaxed" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
        ) : (
          <div className="prose max-w-none text-ink/80 text-lg leading-relaxed whitespace-pre-line reveal">{post.excerpt[lang] || post.excerpt.en}</div>
        )}

        <div className="mt-14 reveal">
          <h2 className="font-serif text-3xl text-wine flex items-center gap-3 mb-6"><HelpCircle className="w-7 h-7 text-gold" /> FAQ</h2>
          <Accordion type="single" collapsible className="bg-white rounded-2xl border border-border px-5">
            {faq.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left font-medium text-ink hover:text-wine">{f.q}</AccordionTrigger>
                <AccordionContent className="text-ink/70 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </article>
    </div>
  );
};

export default BlogDetail;
